import { useRef, useState } from 'react';
import intl from 'react-intl-universal';
import { Icon } from '@getsynapse/design-system';
import { debounce, get } from 'lodash';
import classnames from 'classnames';
import { QuestionsProps } from '../Question';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MB, GB, SYSTEM_MAX_BYTES, toBytes } from 'utils/fileSize';

const PRESETS = [50 * MB, 100 * MB, 250 * MB, 500 * MB, GB];
const T = 'SETTINGS_PAGE.FORMS.DESIGN.CUSTOM_QUESTIONS.FILE';

const pillClass = (active: boolean) =>
  classnames('text-sm rounded-full px-3 py-1 border mr-2 mb-2', {
    'bg-primary text-neutral-white border-primary': active,
    'bg-transparent text-neutral border-neutral-lighter': !active,
  });

const formatPill = (bytes: number) =>
  bytes % GB === 0 ? `${bytes / GB} GB` : `${Math.round(bytes / MB)} MB`;

export const UploadField = ({
  questionData,
  changeQuestionData,
}: QuestionsProps) => {
  const currentBytes: number | null = get(
    questionData,
    'data.maxFileSizeBytes',
    null
  );
  const isCustomValue = currentBytes != null && !PRESETS.includes(currentBytes);
  const [showCustom, setShowCustom] = useState(isCustomValue);
  const [unit, setUnit] = useState<'MB' | 'GB'>('MB');
  const customInputRef = useRef<HTMLInputElement>(null);
  const { requestCustomMaxFileSizeLegacy = false } = useFlags<{
    requestCustomMaxFileSizeLegacy?: boolean;
  }>();

  const updatePlaceholder = debounce((value: string) => {
    changeQuestionData('data.placeholder', value, questionData.id);
  }, 500);

  const setLimit = (bytes: number | null) =>
    changeQuestionData('data.maxFileSizeBytes', bytes, questionData.id);

  const onCustom = debounce((raw: string, selectedUnit: 'MB' | 'GB') => {
    const numeric = Number(raw);
    if (!raw || Number.isNaN(numeric) || numeric <= 0) return;
    setLimit(toBytes(numeric, selectedUnit));
  }, 500);

  return (
    <div data-testid='file-max-size'>
      <div className='px-4 border rounded border-dashed py-2 flex items-center mb-3'>
        <Icon name='cloud-upload' className='text-xl text-neutral mr-6' />
        <input
          placeholder={intl.get(`${T}.PLACEHOLDER`)}
          className='bg-transparent text-base placeholder-neutral focus:outline-none w-full text-neutral focus:text-neutral-black focus:bg-primary-lighter'
          defaultValue={get(questionData, 'data.placeholder')}
          onChange={(event) => updatePlaceholder(event.target.value)}
        />
      </div>

      {requestCustomMaxFileSizeLegacy && (
        <>
          <label
            className='text-sm font-semibold text-neutral-black flex items-center mb-1'
            title={intl.get(`${T}.MAX_SIZE.TOOLTIP`)}
          >
            {intl.get(`${T}.MAX_SIZE.LABEL`)}
            <Icon
              name='information-circle-outline'
              className='ml-1 text-neutral'
            />
          </label>
          <p className='text-xs text-neutral mb-2'>
            {intl.get(`${T}.MAX_SIZE.HELP`)}
          </p>

          <div className='flex flex-wrap items-center'>
            <button
              type='button'
              data-testid='file-max-size__default'
              aria-pressed={currentBytes == null}
              className={pillClass(currentBytes == null)}
              onClick={() => {
                setShowCustom(false);
                setLimit(null);
              }}
            >
              {intl.get(`${T}.MAX_SIZE.DEFAULT`)}
            </button>

            {PRESETS.map((bytes) => (
              <button
                key={bytes}
                type='button'
                data-testid={`file-max-size__preset-${bytes}`}
                aria-pressed={currentBytes === bytes}
                className={pillClass(currentBytes === bytes)}
                onClick={() => {
                  setShowCustom(false);
                  setLimit(bytes);
                }}
              >
                {formatPill(bytes)}
              </button>
            ))}

            <button
              type='button'
              data-testid='file-max-size__custom-toggle'
              aria-pressed={showCustom}
              className={pillClass(showCustom)}
              onClick={() => setShowCustom(true)}
            >
              {intl.get(`${T}.MAX_SIZE.CUSTOM`)}
            </button>
          </div>

          {showCustom && (
            <div className='flex items-center mt-2'>
              <input
                ref={customInputRef}
                type='number'
                min={1}
                data-testid='file-max-size__custom-input'
                className='border rounded px-2 py-1 w-24 mr-2 text-sm'
                defaultValue={
                  isCustomValue && currentBytes != null
                    ? Math.round(currentBytes / MB)
                    : undefined
                }
                onChange={(event) => onCustom(event.target.value, unit)}
              />
              <select
                data-testid='file-max-size__custom-unit'
                className='border rounded px-2 py-1 text-sm'
                value={unit}
                onChange={(event) => {
                  const nextUnit = event.target.value as 'MB' | 'GB';
                  setUnit(nextUnit);
                  onCustom(customInputRef.current?.value ?? '', nextUnit);
                }}
              >
                <option value='MB'>{intl.get(`${T}.MAX_SIZE.UNIT_MB`)}</option>
                <option value='GB'>{intl.get(`${T}.MAX_SIZE.UNIT_GB`)}</option>
              </select>
              <span className='text-xs text-neutral ml-2'>
                (max {formatPill(SYSTEM_MAX_BYTES)})
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UploadField;
